import { progressPct } from "@/lib/progress";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Loader2, ChevronRight, ChevronDown, Activity, ShieldCheck, Map, List, Palette, Star, RotateCcw, AlertTriangle, EyeOff, CalendarOff } from "lucide-react";
import { HEALTH_COLORS, HEALTH_BG, HEALTH_LABELS, formatDuration, phaseColor, MS_SHAPES } from "@/lib/constants";
import ExportButton from "@/components/ExportButton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import RoadmapSwimlane from "@/components/RoadmapSwimlane";
import DebouncedInput from "@/components/DebouncedInput";
import SwatchPicker from "@/components/SwatchPicker";
import StatTasksDialog from "@/components/StatTasksDialog";

const SCALES = {
  fit: { label: "Fit page", pxPerDay: 0, unit: "week" },
  hours: { label: "Hours", pxPerDay: 90, unit: "day" },
  days: { label: "Days", pxPerDay: 40, unit: "day" },
  weeks: { label: "Weeks", pxPerDay: 24, unit: "week" },
  months: { label: "Months", pxPerDay: 6, unit: "month" },
  quarters: { label: "Quarters", pxPerDay: 2.6, unit: "quarter" },
  years: { label: "Years", pxPerDay: 1.2, unit: "year" },
  custom: { label: "Custom", pxPerDay: 16, unit: "week" },
};

const HEALTH = HEALTH_COLORS;
const HEALTH_LABEL = HEALTH_LABELS;
const DEFAULT_TIMESCALE = "#132A5E"; // deep navy
const SEV = { red: 5, yellow: 4, gray: 3, green: 2, blue: 1 };
const worstHealth = (items) => {
  let worst = null;
  items.forEach((t) => { if (!worst || (SEV[t.health] || 0) > SEV[worst]) worst = t.health; });
  return worst || "gray";
};

export default function RoadmapView({ projectId, onOpenTask, durationUnit = "days", phaseColors = {}, progressStyle }) {
  const [data, setData] = useState(null);
  const [holidays, setHolidays] = useState({});
  useEffect(() => {
    api.get(`/holidays?project_id=${projectId}`).then(({ data: d }) => setHolidays(d.holidays || {})).catch(() => {});
  }, [projectId]);
  const [collapsed, setCollapsed] = useState({});
  const [cpOpen, setCpOpen] = useState(true);
  const [mode, setMode] = useState("swimlane");
  const [groupBy, setGroupBy] = useState("phase");
  const [scale, setScale] = useState("weeks");
  const [customPx, setCustomPx] = useState(16);
  const [summarized, setSummarized] = useState(false);
  const [colorOverrides, setColorOverrides] = useState({});
  const laneKey = `pf_laneorder_${projectId}_${groupBy}`;
  const [laneOrder, setLaneOrder] = useState([]);
  useEffect(() => {
    try { setLaneOrder(JSON.parse(localStorage.getItem(laneKey) || "[]")); } catch (e) { setLaneOrder([]); }
  }, [laneKey]);
  const moveLane = (name, dir, currentLanes) => {
    const cur = [...currentLanes];
    const i = cur.indexOf(name), j = i + dir;
    if (i < 0 || j < 0 || j >= cur.length) return;
    [cur[i], cur[j]] = [cur[j], cur[i]];
    localStorage.setItem(laneKey, JSON.stringify(cur));
    setLaneOrder(cur);
  };
  const [statOpen, setStatOpen] = useState(null);
  const [hidden, setHidden] = useState({});
  const toggle = (p) => setCollapsed((c) => ({ ...c, [p]: !c[p] }));

  const hkey = `pf_roadmap_hidden_${projectId}`;
  useEffect(() => {
    try { setHidden(JSON.parse(localStorage.getItem(hkey) || "{}")); } catch { setHidden({}); }
  }, [hkey]);
  const toggleHidden = (id) => setHidden((prev) => {
    const next = { ...prev };
    if (next[id]) delete next[id]; else next[id] = true;
    localStorage.setItem(hkey, JSON.stringify(next));
    return next;
  });
  const showAll = () => { localStorage.removeItem(hkey); setHidden({}); };

  const ckey = `pf_swimcolors_${projectId}`;
  useEffect(() => {
    try { const saved = JSON.parse(localStorage.getItem(ckey) || "{}"); setColorOverrides(saved || {}); }
    catch { setColorOverrides({}); }
  }, [ckey]);
  const setPhaseColor = (phase, color) => {
    setColorOverrides((prev) => {
      const next = { ...prev, [phase]: color };
      localStorage.setItem(ckey, JSON.stringify(next));
      return next;
    });
  };
  const resetColors = () => { localStorage.removeItem(ckey); setColorOverrides({}); };
  const colorFor = useCallback((phase, idx) => colorOverrides[phase] || phaseColors[phase] || phaseColor(phase, idx), [colorOverrides, phaseColors]);
  const bodyColorFor = useCallback((phase, idx) => colorOverrides[`__body_${phase}`] || colorOverrides[phase] || phaseColors[phase] || phaseColor(phase, idx), [colorOverrides, phaseColors]);
  const timescaleColor = colorOverrides.__timescale || DEFAULT_TIMESCALE;
  const fontColor = colorOverrides.__fontcolor || "";
  const fontSize = Number(colorOverrides.__fontsize) || 11;
  const hideDates = !!colorOverrides.__hidedates;
  const labelWrap = Number(colorOverrides.__labelwrap) || 200;
  const chartRef = useRef(null);
  const [wrapW, setWrapW] = useState(0);
  useEffect(() => {
    const measure = () => setWrapW(chartRef.current?.clientWidth || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [mode, data]);

  useEffect(() => {
    api.get(`/projects/${projectId}/critical-path`).then(({ data }) => {
      setData(data);
      // start all phases collapsed
      const phases = [...new Set((data.tasks || []).map((t) => t.phase || "Unphased"))];
      setCollapsed(Object.fromEntries(phases.map((p) => [p, true])));
    }).catch(() => setData({ path: [], tasks: [] }));
  }, [projectId]);

  const [storyMap, setStoryMap] = useState({});
  useEffect(() => {
    api.get(`/projects/${projectId}/stories`).then(({ data: sts }) => {
      const m = {};
      (sts || []).forEach((s) => { if (s.linked_task_id) (m[s.linked_task_id] = m[s.linked_task_id] || []).push(s.display_id); });
      setStoryMap(m);
    }).catch(() => {});
  }, [projectId]);

  const byPhase = useMemo(() => {
    if (!data) return [];
    const groups = {};
    (data.tasks || []).forEach((t) => {
      const p = t.phase || "Unphased";
      (groups[p] = groups[p] || []).push(t);
    });
    return Object.entries(groups);
  }, [data]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const byId = Object.fromEntries(data.tasks.map((t) => [t.id, t]));
  const hiddenCount = Object.keys(hidden).length;
  const visibleTasks = data.tasks.filter((t) => !hidden[t.id]);
  const datedVis = visibleTasks.filter((t) => t.start_date && t.end_date);
  let fitPx = 16;
  if (datedVis.length) {
    let minD = datedVis[0].start_date, maxD = datedVis[0].end_date;
    datedVis.forEach((t) => { if (t.start_date < minD) minD = t.start_date; if (t.end_date > maxD) maxD = t.end_date; });
    const total = Math.max(1, Math.round((Date.parse(maxD) - Date.parse(minD)) / 86400000) + 10);
    fitPx = Math.max(0.5, Math.floor(((wrapW || 1200) - 176) / total * 10) / 10);
  }
  const chain = (data.path || []).map((id) => byId[id]).filter(Boolean);
  const completeCount = data.tasks.filter((t) => t.health === "blue").length;
  const pathSet = new Set(data.path || []);
  const statConfigs = {
    cp: { label: "Critical path tasks", tint: "#EF4444", icon: ShieldCheck, match: (t) => pathSet.has(t.id) },
    at_risk_path: { label: "At risk on the critical path", tint: "#F59E0B", icon: Activity, match: (t) => pathSet.has(t.id) && ["yellow", "red"].includes(t.health) },
    done: { label: "Completed tasks", tint: "#2563EB", icon: ShieldCheck, match: (t) => t.health === "blue" },
  };
  const cpm = data.critical_path_metrics || {};
  const pm = data.project_metrics || {};

  // order phase items so milestones are followed by their child tasks (indented)
  const orderPhaseItems = (items) => {
    const children = {};
    items.forEach((t) => { if (t.parent_id) (children[t.parent_id] = children[t.parent_id] || []).push(t); });
    const out = [];
    items.forEach((t) => {
      if (t.parent_id) return; // handled under parent
      out.push({ t, indent: false });
      (children[t.id] || []).forEach((c) => out.push({ t: c, indent: true }));
    });
    return out;
  };

  if (data.tasks.length === 0) {
    return (
      <div className="border border-dashed border-border rounded-lg p-12 text-center" data-testid="roadmap-empty">
        <Activity className="h-8 w-8 text-primary mx-auto mb-3" />
        <p className="text-muted-foreground">Add tasks with dates and dependencies to build your roadmap.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="roadmap-view">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button type="button" onClick={() => setStatOpen("cp")} className="border border-border rounded-lg p-4 text-left hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid="roadmap-stat-critical">
          <p className="overline text-muted-foreground">Critical tasks</p>
          <p className="font-display text-2xl font-extrabold tracking-tight mt-1">{data.critical_count}</p>
        </button>
        <button type="button" onClick={() => setStatOpen("at_risk_path")} className="border border-border rounded-lg p-4 text-left hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid="roadmap-stat-at-risk">
          <p className="overline text-muted-foreground">At risk on path</p>
          <p className="font-display text-2xl font-extrabold tracking-tight mt-1" style={{ color: data.at_risk ? "#F59E0B" : "#10B981" }}>{data.at_risk}</p>
        </button>
        <button type="button" onClick={() => setStatOpen("cp")} className="border border-border rounded-lg p-4 text-left hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid="roadmap-stat-health">
          <p className="overline text-muted-foreground">Path health</p>
          <div className="flex items-center gap-2 mt-1">
            <span className="h-3.5 w-3.5 rounded-full shrink-0" style={{ background: HEALTH[data.health] }} />
            <p className="font-display text-lg font-extrabold tracking-tight truncate" style={{ color: HEALTH[data.health] }}>{HEALTH_LABEL[data.health]}</p>
          </div>
        </button>
        <button type="button" onClick={() => setStatOpen("done")} className="border border-border rounded-lg p-4 text-left hover:border-foreground/30 hover:shadow-sm transition-all cursor-pointer" data-testid="roadmap-stat-complete">
          <p className="overline text-muted-foreground">Complete</p>
          <p className="font-display text-2xl font-extrabold tracking-tight mt-1" style={{ color: "#2563EB" }}>{completeCount}</p>
        </button>
      </div>

      {/* Duration & % complete: critical path vs whole project */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="duration-metrics">
        <div className="border border-border rounded-lg p-5" data-testid="cp-duration-card" style={{ borderLeft: "4px solid #EF4444" }}>
          <p className="overline text-muted-foreground">Critical path</p>
          <div className="flex items-baseline gap-3 mt-2 flex-wrap">
            <span className="font-display text-3xl font-extrabold tracking-tight">{formatDuration(cpm.working_days || 0, durationUnit)}</span>
            <span className="text-sm text-muted-foreground">{cpm.calendar_days || 0} calendar days</span>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">% complete</span><span className="font-semibold">{cpm.percent_complete || 0}%</span></div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${cpm.percent_complete || 0}%`, background: "#EF4444" }} /></div>
          </div>
          {cpm.start && <p className="text-[11px] text-muted-foreground mt-2">{cpm.start} → {cpm.end}</p>}
        </div>
        <div className="border border-border rounded-lg p-5" data-testid="project-duration-card" style={{ borderLeft: "4px solid #0044FF" }}>
          <p className="overline text-muted-foreground">Overall project</p>
          <div className="flex items-baseline gap-3 mt-2 flex-wrap">
            <span className="font-display text-3xl font-extrabold tracking-tight">{formatDuration(pm.working_days || 0, durationUnit)}</span>
            <span className="text-sm text-muted-foreground">{pm.calendar_days || 0} calendar days</span>
          </div>
          <div className="mt-3">
            <div className="flex justify-between text-xs mb-1"><span className="text-muted-foreground">% complete</span><span className="font-semibold">{pm.percent_complete || 0}%</span></div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden"><div className="h-full rounded-full" style={{ width: `${pm.percent_complete || 0}%`, background: "#0044FF" }} /></div>
          </div>
          {pm.start && <p className="text-[11px] text-muted-foreground mt-2">{pm.start} → {pm.end}</p>}
        </div>
      </div>

      {/* Critical path flowchart */}
      {chain.length > 0 && (
      <div className="border border-border rounded-lg p-6">
        <button type="button" onClick={() => { setCpOpen((v) => !v); localStorage.setItem("cp-seen", "1"); }} className="w-full flex items-center gap-2 mb-1 text-left group" data-testid="critical-path-toggle">
          {cpOpen ? <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" /> : <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0" />}
          <h3 className="font-display text-lg font-bold flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-primary" /> Critical path</h3>
          <span className="text-[11px] text-muted-foreground font-normal">· {chain.length} tasks</span>
          {!cpOpen && !localStorage.getItem("cp-seen") && (
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide bg-primary text-white px-2 py-0.5 rounded-full animate-pulse" data-testid="cp-discover-badge">
              Click to view your critical path
            </span>
          )}
        </button>
        {cpOpen && (<>
        <p className="text-xs text-muted-foreground mb-5 pl-6">The longest chain of dependent work. Each box is colored by its health — anything slipping turns yellow or red.</p>
        <div className="flex flex-wrap items-stretch gap-y-4" data-testid="critical-path-flow">
          {chain.map((t, i) => (
            <div key={t.id} className="flex items-center">
              <button
                onClick={() => onOpenTask?.(byId[t.id])}
                className="w-52 text-left rounded-lg shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all p-3 text-white"
                style={{ background: HEALTH[t.health], boxShadow: `0 6px 16px -6px ${HEALTH[t.health]}99`, border: t.is_critical ? "2px solid rgba(255,255,255,0.5)" : "none" }}
                data-testid={`cp-node-${t.id}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-white/80 truncate">{t.phase}</span>
                  {["at_risk", "jeopardy"].includes(t.track_status) ? (
                    <span className="text-[8px] font-extrabold px-1.5 py-0.5 rounded-full bg-white/95 shrink-0" style={{ color: t.track_status === "jeopardy" ? "#EF4444" : "#D97706" }} data-testid={`cp-track-${t.id}`}>
                      {t.track_status === "jeopardy" ? "JEOPARDY" : "AT RISK"}
                    </span>
                  ) : (
                    <span className="h-2.5 w-2.5 rounded-full shrink-0 bg-white/90" />
                  )}
                </div>
                <p className="text-sm font-semibold leading-snug line-clamp-2">{t.title}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  <div className="flex-1 h-1.5 rounded-full bg-white/30 overflow-hidden"><div className="h-full rounded-full bg-white" style={{ width: `${t.percent_complete}%` }} /></div>
                  <span className="text-[11px] font-bold text-white shrink-0">{t.percent_complete}%</span>
                </div>
                <p className="text-[11px] text-white/75 mt-1">{t.end_date ? `due ${t.end_date}` : "no date"}{t.assignee ? ` · ${t.assignee}` : ""}</p>
              </button>
              {i < chain.length - 1 && <ChevronRight className="h-5 w-5 text-muted-foreground mx-1 shrink-0" />}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-4 mt-6 text-xs">
          {["blue", "green", "yellow", "red", "gray"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH[k] }} />{HEALTH_LABEL[k]}</span>
          ))}
        </div>
        </>)}
      </div>
      )}

      {/* View toggle: Swimlane (Visio) vs List */}
      <div className="flex items-center gap-1 border border-border rounded-lg p-1 w-fit" data-testid="roadmap-view-toggle">
        <button onClick={() => setMode("swimlane")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${mode === "swimlane" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-mode-swimlane"><Map className="h-4 w-4" /> Swimlane</button>
        <button onClick={() => setMode("list")} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${mode === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-mode-list"><List className="h-4 w-4" /> List</button>
        {mode === "swimlane" && (
          <span className="flex items-center gap-1 ml-2 pl-2 border-l border-border">
            <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Lanes:</span>
            <button onClick={() => setGroupBy("phase")} className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${groupBy === "phase" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-group-phase">Phase</button>
            <button onClick={() => setGroupBy("swimlane")} className={`px-2 py-1 rounded text-xs font-semibold transition-colors ${groupBy === "swimlane" ? "bg-secondary text-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-group-swimlane" title="Group by the Swimlane / Sub-swimlane set on each task">Swimlane</button>
          </span>
        )}
      </div>

      {mode === "swimlane" && (
        <>
          <div className="flex items-center gap-3 flex-wrap" data-testid="roadmap-controls">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Timescale</span>
              <Select value={scale} onValueChange={setScale}>
                <SelectTrigger className="h-9 w-36" data-testid="roadmap-timescale-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(SCALES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`timescale-${k}`}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
              {scale === "custom" && (
                <div className="flex items-center gap-1.5" data-testid="roadmap-custom-px">
                  <input type="range" min="2" max="80" value={customPx} onChange={(e) => setCustomPx(Number(e.target.value))} className="w-28" data-testid="roadmap-custom-slider" />
                  <span className="text-xs tabular-nums text-muted-foreground w-14">{customPx}px/day</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-1 border border-border rounded-lg p-1" data-testid="roadmap-detail-toggle">
              <button onClick={() => setSummarized(false)} className={`px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${!summarized ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-view-full">Full</button>
              <button onClick={() => setSummarized(true)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-semibold transition-all ${summarized ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`} data-testid="roadmap-view-summarized"><Star className="h-3.5 w-3.5" /> Summarized</button>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors" data-testid="roadmap-colors-button"><Palette className="h-4 w-4" /> Colors</button>
              </PopoverTrigger>
              <PopoverContent className="w-80" data-testid="roadmap-colors-popover">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-sm font-bold">Swimlane colors</p>
                  <button onClick={resetColors} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1" data-testid="roadmap-colors-reset"><RotateCcw className="h-3 w-3" /> Reset</button>
                </div>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  <div className="flex items-center justify-between gap-2 pb-2 mb-1 border-b border-border">
                    <span className="text-sm font-semibold">Timescale bar</span>
                    <SwatchPicker value={timescaleColor} onChange={(v) => setPhaseColor("__timescale", v)} title="Timescale color" testid="roadmap-color-timescale" />
                  </div>
                  <div className="pb-2 mb-1 border-b border-border space-y-2" data-testid="roadmap-font-controls">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">Font color</span>
                      <div className="flex items-center gap-1.5">
                        {fontColor && <button onClick={() => setPhaseColor("__fontcolor", "")} className="text-[10px] text-muted-foreground hover:text-foreground underline" data-testid="roadmap-font-color-auto">Auto</button>}
                        <SwatchPicker value={fontColor || "#0f172a"} onChange={(v) => setPhaseColor("__fontcolor", v)} title="Font color" testid="roadmap-font-color" />
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">Font size</span>
                      <div className="flex items-center gap-1.5">
                        <DebouncedInput type="range" min="9" max="18" value={fontSize} onCommit={(v) => setPhaseColor("__fontsize", v)} className="w-24" data-testid="roadmap-font-size" />
                        <span className="text-xs tabular-nums text-muted-foreground w-9">{fontSize}px</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">Label wrap</span>
                      <div className="flex items-center gap-1.5">
                        <DebouncedInput type="range" min="100" max="400" value={labelWrap} onCommit={(v) => setPhaseColor("__labelwrap", v)} className="w-24" data-testid="roadmap-label-wrap" />
                        <span className="text-xs tabular-nums text-muted-foreground w-12">{labelWrap}px</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-end gap-5 pr-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    <span>Header</span><span>Body</span>
                  </div>
                  {byPhase.map(([phase], idx) => (
                    <div key={phase} className="flex items-center justify-between gap-2">
                      <span className="text-sm truncate flex-1">{phase}</span>
                      <SwatchPicker value={colorFor(phase, idx)} onChange={(v) => setPhaseColor(phase, v)} title="Header color" testid={`roadmap-color-${phase}`} />
                      <SwatchPicker value={bodyColorFor(phase, idx)} onChange={(v) => setPhaseColor(`__body_${phase}`, v)} title="Body color" testid={`roadmap-color-body-${phase}`} />
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1.5 h-9 px-3 rounded-lg border border-border text-sm font-semibold text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors" data-testid="roadmap-hide-button">
                  <EyeOff className="h-4 w-4" /> Hide tasks{hiddenCount ? ` (${hiddenCount})` : ""}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-96" data-testid="roadmap-hide-popover">
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-bold">Show / hide on chart</p>
                  {hiddenCount > 0 && <button onClick={showAll} className="text-xs text-primary font-semibold hover:underline" data-testid="roadmap-show-all">Show all</button>}
                </div>
                <p className="text-xs text-muted-foreground mb-2">Uncheck tasks to remove them from the chart. Your choices are remembered.</p>
                <div className="space-y-3 max-h-72 overflow-y-auto">
                  {byPhase.map(([phase, items]) => (
                    <div key={phase}>
                      <p className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">{phase}</p>
                      <div className="space-y-0.5">
                        {items.map((t) => (
                          <label key={t.id} className="flex items-center gap-2 text-sm cursor-pointer rounded px-1 py-0.5 hover:bg-secondary/50" data-testid={`roadmap-hide-item-${t.id}`}>
                            <input type="checkbox" className="accent-primary" checked={!hidden[t.id]} onChange={() => toggleHidden(t.id)} />
                            <span className={`truncate ${hidden[t.id] ? "text-muted-foreground line-through" : ""}`}>{t.is_milestone ? "▼ " : ""}{t.title}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            <button onClick={() => setPhaseColor("__hidedates", hideDates ? "" : "1")}
              className={`flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-semibold transition-colors ${hideDates ? "border-primary text-primary bg-primary/5" : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/60"}`}
              data-testid="roadmap-hide-dates-toggle">
              <CalendarOff className="h-4 w-4" /> {hideDates ? "Dates hidden" : "Hide dates"}
            </button>

            <ExportButton targetRef={chartRef} filename="roadmap" label="Export" />
          </div>

          <div ref={chartRef}>
            <RoadmapSwimlane
              tasks={groupBy === "swimlane"
                ? visibleTasks.map((t) => ({ ...t, phase: (t.swimlane || "No swimlane") + (t.sub_swimlane ? ` · ${t.sub_swimlane}` : "") }))
                : visibleTasks}
              onOpenTask={onOpenTask}
              pxPerDay={scale === "custom" ? customPx : scale === "fit" ? fitPx : SCALES[scale].pxPerDay}
              tickUnit={SCALES[scale].unit}
              summarized={summarized}
              colorFor={colorFor}
              bodyColorFor={bodyColorFor}
              timescaleColor={timescaleColor}
              fontColor={fontColor}
              fontSize={fontSize}
              holidays={holidays}
              hideDates={hideDates}
              labelWrap={labelWrap}
              storyIds={storyMap}
              laneOrder={laneOrder}
              onMoveLane={moveLane}
            />
          </div>
        </>
      )}

      {mode === "list" && (<>
      {/* Progress roadmap by phase */}
      <div className="border border-border rounded-lg p-6" data-testid="roadmap-progress">
        <h3 className="font-display text-lg font-bold flex items-center gap-2 mb-1"><Map className="h-5 w-5 text-primary" /> Progress roadmap</h3>
        <p className="text-xs text-muted-foreground mb-5">Every task colored by health and confirmation. Gray needs PM confirmation, blue is complete, and the bar shows confirmed % against expected.</p>
        <div className="space-y-6">
          {byPhase.map(([phase, items]) => {
            const avg = progressPct(items, progressStyle);
            const ph = worstHealth(items);
            const isOpen = !collapsed[phase];
            const rows = orderPhaseItems(items);
            return (
            <div key={phase} data-testid={`roadmap-phase-${phase}`}>
              <button onClick={() => toggle(phase)} className="w-full flex items-center gap-2 mb-2 group" data-testid={`roadmap-phase-toggle-${phase}`}>
                {isOpen ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                <span className="h-3 w-3 rounded-full shrink-0" style={{ background: HEALTH[ph] }} title={`Phase health: ${HEALTH_LABEL[ph]}`} data-testid={`phase-health-${phase}`} />
                <span className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground group-hover:text-foreground">{phase}</span>
                <span className="text-[10px] text-muted-foreground font-normal">· {items.length} · {avg}% · <span style={{ color: HEALTH[ph] }}>{HEALTH_LABEL[ph]}</span></span>
                <span className="h-px flex-1 bg-border ml-2" />
              </button>
              {isOpen && (
              <div className="space-y-2">
                {rows.map(({ t, indent }) => (
                  <button
                    key={t.id}
                    onClick={() => onOpenTask?.(byId[t.id])}
                    className="w-full text-left rounded-lg border border-border bg-card hover:shadow-sm hover:-translate-y-0.5 transition-all p-3"
                    style={{ borderLeftWidth: 4, borderLeftColor: HEALTH[t.health], marginLeft: indent ? 24 : 0, width: indent ? "calc(100% - 24px)" : "100%" }}
                    data-testid={`roadmap-task-${t.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="shrink-0" style={{ color: HEALTH[t.health] }} title={HEALTH_LABEL[t.health]}>
                        {t.is_milestone ? <span className="text-sm leading-none" style={{ color: HEALTH[t.health] || t.ms_color }}>{MS_SHAPES[t.ms_shape] || "▼"}</span> : <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: HEALTH[t.health] }} />}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          {indent && <span className="text-muted-foreground text-xs">↳</span>}
                          <p className="text-sm font-semibold truncate">{t.title}</p>
                          {t.is_milestone && <span className="text-[10px] font-bold text-primary shrink-0">milestone</span>}
                          {t.is_critical && <span className="text-[10px] font-bold text-red-500 shrink-0">★ critical</span>}
                          {t.health === "gray" && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">needs confirmation</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 text-[11px] text-muted-foreground">
                          <span>{t.start_date || "—"} → {t.end_date || "—"}</span>
                          {t.assignee && <span>· 👤 {t.assignee}</span>}
                        </div>
                        {!t.is_milestone && (
                          <div className="mt-1.5 h-1.5 w-full rounded-full bg-secondary overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{ width: `${t.percent_complete}%`, background: HEALTH[t.health] }} />
                          </div>
                        )}
                      </div>
                      <div className="text-right shrink-0 w-24">
                        <p className="text-sm font-bold" style={{ color: HEALTH[t.health] }}>{t.percent_complete}%</p>
                        <p className="text-[10px] text-muted-foreground">{t.expected_percent != null ? `exp ${t.expected_percent}%` : (t.end_date ? "" : "no date")}</p>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              )}
            </div>
            );
          })}
        </div>
        <div className="flex flex-wrap gap-4 mt-6 text-xs">
          {["blue", "green", "yellow", "red", "gray"].map((k) => (
            <span key={k} className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full" style={{ background: HEALTH[k] }} />{HEALTH_LABEL[k]}</span>
          ))}
        </div>
      </div>
      </>)}

      <AtRiskList tasks={data.tasks} />
      <StatTasksDialog open={!!statOpen} onOpenChange={(v) => !v && setStatOpen(null)} config={statOpen ? statConfigs[statOpen] : null} tasks={data.tasks} onOpenTask={(t) => { setStatOpen(null); onOpenTask?.(t); }} />
    </div>
  );
}

export function AtRiskList({ tasks }) {
  const flagged = (tasks || []).filter((t) => ["yellow", "red"].includes(t.health));
  if (flagged.length === 0) return null;
  return (
    <div className="border border-amber-200 bg-amber-50/50 rounded-lg p-5" data-testid="atrisk-bullets">
      <h3 className="font-display font-bold flex items-center gap-2 mb-3 text-sm">
        <AlertTriangle className="h-4 w-4 text-amber-500" /> Risk items on this view ({flagged.length})
      </h3>
      <ul className="space-y-1.5">
        {flagged.map((t) => (
          <li key={t.id} className="flex items-start gap-2 text-sm" data-testid={`atrisk-item-${t.id}`}>
            <span className="h-2.5 w-2.5 rounded-full mt-1.5 shrink-0" style={{ background: t.health === "red" ? "#EF4444" : "#F59E0B" }} />
            <span className="min-w-0">
              <b>{t.is_milestone ? "◆ " : ""}{t.title}</b>
              <span className="text-muted-foreground"> — {t.health === "red" ? "slipping (red)" : "at risk (yellow)"}{t.assignee ? ` · ${t.assignee}` : ""}{t.end_date ? ` · due ${t.end_date}` : ""}</span>
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
